import { createFilterID } from 'core/utils';
import { useFinancials, useFinancialsActions } from 'hooks/financials';
import * as React from 'react';
import FinancialsTableFilters from '../financials-table-filters';
import {
  DATE_RANGE_ACTIONS_OPTIONS_FOR_AMPLITUDE,
  OUTSTANDING_STATUS,
  OutstandingReportRow,
} from '../types';

interface Props {
  displayExportModal: () => void;
  handleOnCaptureDateRangeAction: (
    id: keyof typeof DATE_RANGE_ACTIONS_OPTIONS_FOR_AMPLITUDE
  ) => void;
  trackOnClearForAmplitude: () => void;
}

export function OutstandingFilters({
  displayExportModal,
  handleOnCaptureDateRangeAction,
  trackOnClearForAmplitude,
}: Props) {
  const { outstandingFilters, outstandingReports } = useFinancials();
  const { updateOutstandingFilter, setOutstandingFilters } =
    useFinancialsActions();
  const {
    programOptions,
    sourceTypeOptions,
    statusOptions,
    registrationOptions,
    invoiceOptions,
  } = useGetOptionsForComboboxes(outstandingReports);

  return (
    <FinancialsTableFilters
      filters={outstandingFilters}
      setFilter={updateOutstandingFilter}
      onClearAll={() => {
        trackOnClearForAmplitude();
        setOutstandingFilters({
          ...outstandingFilters,
          program: [],
          sourceType: [],
          registration: [],
          invoice: [],
          status: [],
        });
      }}
      comboboxFilters={[
        {
          key: 'program',
          label: 'Program',
          options: programOptions,
        },
        {
          key: 'sourceType',
          label: 'Source Type',
          options: sourceTypeOptions,
        },
        {
          key: 'registration',
          label: 'Registrations',
          options: registrationOptions,
        },
        {
          key: 'invoice',
          label: 'Invoices',
          options: invoiceOptions,
        },
        {
          key: 'status',
          label: 'Status',
          options: statusOptions,
        },
      ]}
      onExport={displayExportModal}
      onCaptureDateRangeAction={handleOnCaptureDateRangeAction}
    />
  );
}

const useGetOptionsForComboboxes = (
  outstandingReportList: OutstandingReportRow[]
) => {
  const programOptions = React.useMemo(() => {
    if (!outstandingReportList) return null;
    const programs = outstandingReportList.flatMap((row) =>
      row.sourceItems?.map((item) => item.programSeason)
    );
    return getFilteredAndSortedValueAndLabelFromList({
      list: programs,
      emptyLabel: 'No Program',
    });
  }, [outstandingReportList]);

  const registrationOptions = React.useMemo(() => {
    if (!outstandingReportList) return null;
    const registration = outstandingReportList
      .filter((row) => row.orderType === 'Registration')
      .map((row) => row.sourceItems[0]?.programSeason);
    return getFilteredAndSortedValueAndLabelFromList({
      list: registration,
      emptyLabel: 'No Registration',
    });
  }, [outstandingReportList]);

  const invoiceOptions = React.useMemo(() => {
    if (!outstandingReportList) return null;
    const invoices = outstandingReportList
      .filter((row) => row.orderType === 'Invoice')
      .map((row) => row.sourceItems[0]?.programSeason);
    return getFilteredAndSortedValueAndLabelFromList({
      list: invoices,
      emptyLabel: 'No Invoice',
    });
  }, [outstandingReportList]);

  return {
    programOptions,
    sourceTypeOptions: [
      { value: 'invoice', label: 'Invoice' },
      { value: 'registration', label: 'Registration' },
    ],
    registrationOptions,
    invoiceOptions,
    statusOptions: [
      {
        value: OUTSTANDING_STATUS.DUE,
        label: OUTSTANDING_STATUS.DUE,
      },
      {
        value: OUTSTANDING_STATUS.PAST_DUE,
        label: OUTSTANDING_STATUS.PAST_DUE,
      },
      {
        value: OUTSTANDING_STATUS.PENDING_CASH_PAYMENT,
        label: OUTSTANDING_STATUS.PENDING_CASH_PAYMENT,
      },
      {
        value: OUTSTANDING_STATUS.SCHEDULED,
        label: OUTSTANDING_STATUS.SCHEDULED,
      },
    ],
  };
};

function getFilteredAndSortedValueAndLabelFromList({
  list,
  emptyLabel = '-',
}: {
  list: string[];
  emptyLabel: string;
}) {
  return list
    .filter((value, index) => list.indexOf(value) === index)
    .sort((x, y) => (x > y ? 1 : -1))
    .map((value) => ({
      label: value || emptyLabel,
      value: value ? createFilterID(value) : null,
    }));
}
